/**
 * Free-text search for the historic command log.
 *
 * `cmd_logs_v1` is mapped with `dynamic: "false"` and its string fields, including
 * `command` and `fromPlayerName`, are root-level `keyword` fields with no analysed
 * text sibling. A `match`/`multi_match` on a keyword field only hits when the whole
 * stored value equals the query, so searching "maintenance_hatch" never found
 * "maintenance_hatch goonsquad tp …" even though the rows were right there.
 * Substring matching therefore has to be done with wildcard queries on the keyword
 * values, which Elasticsearch can run case-insensitively.
 *
 * Every whitespace-separated token in the search box must appear somewhere in the
 * command or the player name, so adding a token narrows the result like every
 * other filter in the bar does.
 */

export const COMMAND_TEXT_SEARCH_FIELDS = ['command', 'fromPlayerName'] as const;

/** Escape the wildcard operators so the user's text is matched literally. */
export function escapeWildcard(value: string): string {
  return value.replace(/[\\*?]/g, '\\$&');
}

/** The distinct, non-blank tokens the search box holds. */
export function commandTextSearchTokens(textSearch: string): string[] {
  return Array.from(new Set(textSearch.split(/\s+/).filter(Boolean)));
}

/**
 * One `must` clause per token: a case-insensitive "contains" match against each
 * searchable field, of which at least one has to hit. Empty input yields no clauses.
 */
export function commandTextSearchClauses(textSearch: string): object[] {
  return commandTextSearchTokens(textSearch).map((token) => ({
    bool: {
      should: COMMAND_TEXT_SEARCH_FIELDS.map((field) => ({
        wildcard: { [field]: { value: `*${escapeWildcard(token)}*`, case_insensitive: true } },
      })),
      minimum_should_match: 1,
    },
  }));
}
